import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ShieldCheck,
  Eye,
  EyeOff,
  Plug,
  RefreshCw,
  Trash2,
  ExternalLink,
  Zap,
  Truck,
  TrendingUp,
  Image as ImageIcon,
  FileCheck2,
  IndianRupee,
} from "lucide-react";
import { AppShell } from "../components/AppShell";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";

/**
 * Connect Myntra Partner Portal — multi-tenant.
 *
 * Each seller saves their OWN Myntra Partner Portal credentials. They
 * are encrypted at rest with Fernet (`MYNTRA_VAULT_KEY` on the backend)
 * and never sent back to the client.
 *
 * Status state machine (returned by /status):
 *   none          → seller has never connected
 *   pending_test  → saved, awaiting first verification
 *   connected     → last login succeeded
 *   needs_2fa     → captcha / OTP appeared, seller must intervene
 *   bad_credentials | network_error | ui_change | unknown | error
 */

const FEATURES = [
  {
    icon: Zap,
    title: "Auto-Upload SKUs",
    desc: "Generated Excel → uploaded to Myntra in one click. No manual portal navigation.",
  },
  {
    icon: TrendingUp,
    title: "Daily Sales Sync",
    desc: "Sales, orders, RTO and ad spend pulled every morning into your dashboard.",
  },
  {
    icon: IndianRupee,
    title: "Smart Repricing",
    desc: "Watches competitor prices; bumps your MRP/ISP automatically to keep the badge.",
  },
  {
    icon: Truck,
    title: "Live Inventory",
    desc: "Push stock updates from your master sheet in real-time.",
  },
  {
    icon: ImageIcon,
    title: "Image-Approval Watcher",
    desc: "Detects QC rejections and surfaces the failing SKU so you can fix and re-upload.",
  },
  {
    icon: TrendingUp,
    title: "Deep Performance Insights",
    desc: "Category rank, search position, bestseller badges — daily trend.",
  },
  {
    icon: FileCheck2,
    title: "Auto-Claim Management",
    desc: "Files lost-shipment / wrong-return claims with screenshots automatically.",
  },
];

export default function MyntraConnect() {
  const [creds, setCreds] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [brandId, setBrandId] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  async function fetchStatus() {
    try {
      const { data } = await api.get("/seller/integrations/myntra/status");
      setCreds(data.credentials);
      if (data.credentials?.email) {
        // Pre-fill the form so editing is easy
        // (we only get the actual email back, never the password)
        setEmail(data.credentials.email);
        setBrandId(data.credentials.brand_id || "");
      }
    } catch (e) {
      // 401 handled by axios interceptor — anything else is silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
  }, []);

  async function handleSave(e) {
    e?.preventDefault();
    if (!email || !password) {
      toast.error("Email aur password dono chahiye");
      return;
    }
    setSaving(true);
    try {
      await api.post("/seller/integrations/myntra/connect", {
        email,
        password,
        brand_id: brandId || null,
      });
      toast.success("Credentials saved securely 🔐");
      setPassword("");
      await fetchStatus();
      // Auto-trigger a connection test immediately
      handleTest();
    } catch (err) {
      toast.error(errMsg(err, "Could not save credentials"));
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      const { data } = await api.post("/seller/integrations/myntra/test");
      if (data.ok) {
        toast.success(data.message || "Connected to Myntra ✅");
      } else {
        toast.error(data.message || "Could not connect");
      }
      await fetchStatus();
    } catch (err) {
      toast.error(errMsg(err, "Test failed"));
    } finally {
      setTesting(false);
    }
  }

  async function handleDisconnect() {
    if (!window.confirm("Disconnect your Myntra account? All automations will stop.")) {
      return;
    }
    try {
      await api.delete("/seller/integrations/myntra/disconnect");
      toast.success("Myntra disconnected");
      setCreds(null);
      setEmail("");
      setPassword("");
      setBrandId("");
    } catch (err) {
      toast.error(errMsg(err, "Could not disconnect"));
    }
  }

  const status = creds?.status || (creds ? "pending_test" : "none");
  const statusMeta = getStatusMeta(status);

  return (
    <AppShell role="seller">
      <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
              style={{
                background:
                  "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)",
              }}
              data-testid="myntra-icon"
            >
              M
            </div>
            <div>
              <h1
                className="text-3xl font-bold tracking-tight"
                style={{ color: "#282C3F", WebkitTextFillColor: "#282C3F" }}
                data-testid="myntra-connect-title"
              >
                Connect Myntra Partner Portal
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                One-time setup — unlocks 7 automations for your brand.
              </p>
            </div>
          </div>

          {/* Status pill */}
          <div className="mt-6">
            <span
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold border ${statusMeta.classes}`}
              data-testid="myntra-status-pill"
            >
              <statusMeta.Icon size={14} />
              {statusMeta.label}
            </span>
            {creds?.email_masked && (
              <span className="ml-3 text-sm text-slate-600">
                Linked to <span className="font-semibold text-slate-800">{creds.email_masked}</span>
              </span>
            )}
            {creds?.last_login_at && (
              <span className="ml-3 text-xs text-slate-400">
                Last verified {new Date(creds.last_login_at).toLocaleString()}
              </span>
            )}
          </div>

          {creds?.last_error && status !== "connected" && (
            <div
              className="mt-4 p-3 rounded-xl border bg-rose-50 border-rose-200 text-sm text-rose-700"
              data-testid="myntra-last-error"
            >
              <strong>Last error:</strong> {creds.last_error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
            {/* Form card */}
            <div className="lg:col-span-3">
              <form
                onSubmit={handleSave}
                className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] p-6"
                data-testid="myntra-connect-form"
              >
                <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Plug size={16} style={{ color: "#FF3F6C" }} />
                  {creds ? "Update credentials" : "Save credentials"}
                </h2>

                {/* Email */}
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  Myntra Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="brand@yourcompany.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#FF3F6C] focus:ring-2 focus:ring-[#FF3F6C]/15 outline-none transition"
                  required
                  data-testid="myntra-email-input"
                />

                {/* Password */}
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 mt-4">
                  Myntra Password
                </label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={creds ? "Re-enter to update" : "Your Partner Portal password"}
                    className="w-full px-4 py-2.5 pr-11 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#FF3F6C] focus:ring-2 focus:ring-[#FF3F6C]/15 outline-none transition"
                    data-testid="myntra-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="myntra-password-toggle"
                  >
                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Brand ID (optional) */}
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 mt-4">
                  Brand ID <span className="text-slate-400 normal-case font-medium">(optional)</span>
                </label>
                <input
                  type="text"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  placeholder="If you have multiple brands"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-[#FF3F6C] focus:ring-2 focus:ring-[#FF3F6C]/15 outline-none transition"
                  data-testid="myntra-brand-input"
                />

                {/* Security note */}
                <div className="mt-4 flex items-start gap-2 p-3 rounded-xl bg-[#FFF2F4] border border-[#FFD3DE]">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0" style={{ color: "#FF3F6C" }} />
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Password is <strong>encrypted at rest</strong> with Fernet and decrypted only when the bot needs it. Disconnect anytime — wipes the record from our DB.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3 mt-6">
                  <button
                    type="submit"
                    disabled={saving || (!password && !creds)}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{
                      background:
                        "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)",
                    }}
                    data-testid="myntra-save-btn"
                  >
                    {saving ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="animate-spin" size={14} />
                        Saving…
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <Plug size={14} />
                        {creds ? "Update & test" : "Save & test connection"}
                      </span>
                    )}
                  </button>

                  {creds && (
                    <button
                      type="button"
                      onClick={handleTest}
                      disabled={testing}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 transition-all inline-flex items-center gap-2"
                      data-testid="myntra-test-btn"
                    >
                      {testing ? (
                        <>
                          <Loader2 className="animate-spin" size={14} />
                          Testing…
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} />
                          Test connection
                        </>
                      )}
                    </button>
                  )}

                  {creds && (
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 transition-all inline-flex items-center gap-2"
                      data-testid="myntra-disconnect-btn"
                    >
                      <Trash2 size={14} />
                      Disconnect
                    </button>
                  )}
                </div>

                <a
                  href="https://partners.myntra.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-5 text-xs text-slate-500 hover:text-[#FF3F6C] transition-colors"
                  data-testid="myntra-portal-link"
                >
                  Open Myntra Partner Portal
                  <ExternalLink size={11} />
                </a>
              </form>
            </div>

            {/* Features card */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] p-6 sticky top-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  Unlocks
                </div>
                <ul className="space-y-3">
                  {FEATURES.map(({ icon: Icon, title, desc }) => (
                    <li
                      key={title}
                      className="flex items-start gap-3"
                      data-testid={`myntra-feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{
                          background: "#FFF2F4",
                          color: "#FF3F6C",
                        }}
                      >
                        <Icon size={14} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {title}
                        </div>
                        <div className="text-xs text-slate-500 leading-relaxed">
                          {desc}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {loading && (
            <div className="mt-6 text-sm text-slate-400 inline-flex items-center gap-2">
              <Loader2 className="animate-spin" size={14} />
              Loading current status…
            </div>
          )}
      </div>
    </AppShell>
  );
}

function getStatusMeta(status) {
  switch (status) {
    case "connected":
      return {
        label: "Connected",
        Icon: CheckCircle2,
        classes:
          "bg-emerald-50 text-emerald-700 border-emerald-200",
      };
    case "needs_2fa":
      return {
        label: "Needs 2FA / Captcha",
        Icon: ShieldCheck,
        classes: "bg-amber-50 text-amber-700 border-amber-200",
      };
    case "bad_credentials":
      return {
        label: "Bad credentials",
        Icon: XCircle,
        classes: "bg-rose-50 text-rose-700 border-rose-200",
      };
    case "pending_test":
      return {
        label: "Awaiting first test",
        Icon: Loader2,
        classes: "bg-slate-50 text-slate-700 border-slate-200",
      };
    case "none":
      return {
        label: "Not connected",
        Icon: Plug,
        classes: "bg-slate-50 text-slate-500 border-slate-200",
      };
    default:
      return {
        label: status.replace(/_/g, " "),
        Icon: XCircle,
        classes: "bg-rose-50 text-rose-700 border-rose-200",
      };
  }
}
